<template>
  <v-dialog persistent min-width="600" v-model="showOnboardingDialog">
    <v-stepper v-model="step">
      <template #default="{ prev, next }">
        <h2 class="text-center">Onboarding clock:it</h2>
        <v-stepper-header>
          <v-stepper-item :value="1" :title="t('title.terminalsystems')" editable> </v-stepper-item>
          <v-divider></v-divider>
          <v-stepper-item :value="2" :title="t('title.connectionparameters')" editable> </v-stepper-item>
        </v-stepper-header>
        <v-stepper-window>
          <v-stepper-window-item :value="1">
            <v-toolbar color="#6b4da9" flat>
              <v-toolbar-title>{{ t('title.terminalSystems') }}</v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn icon @click="showTsDialog()" :disabled="selection?.length <= 0">
                <v-icon icon="mdi-pencil"></v-icon>
                <v-tooltip open-delay="300" activator="parent" location="top">{{ t('button.edit') }}</v-tooltip>
              </v-btn>
            </v-toolbar>
            <terminal-system-table
              v-model="terminalSystems"
              v-model:selection="selection"
              v-model:system="system"
              v-model:search="search"
              v-model:loading="loading"
              v-model:sections="sections"
              v-model:page="page"
              height=""
              hide-footer
              @reload-items="loadTerminalSystems()"
            ></terminal-system-table>
            <terminal-system-dialog
              v-model:showDialog="showTsEditDialog"
              v-model:system="system"
              :sections="sections"
              @onCancel="() => (showTsEditDialog = false)"
              @onAccept="saveTerminalSystem()"
            ></terminal-system-dialog>
          </v-stepper-window-item>
          <v-stepper-window-item :value="2">
            <v-toolbar color="#6b4da9" flat>
              <v-toolbar-title>{{ t('model.connection') }}</v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn icon @click="showCpDialog()" :disabled="selection?.length <= 0">
                <v-icon icon="mdi-pencil"></v-icon>
                <v-tooltip open-delay="300" activator="parent" location="top">{{ t('button.edit') }}</v-tooltip>
              </v-btn>
            </v-toolbar>
            <connection-parameter-table
              v-model:loading="loading"
              v-model:selection="selection"
              v-model:sections="sections"
              v-model:terminalSystems="terminalSystems"
              v-model:connectionParameters="connectionParameters"
              v-model:page="page"
              v-model:search="search"
              @reload-items="loadConnectionParameters()"
              height=""
              hide-footer
            ></connection-parameter-table>
            <connection-parameter-dialog
              v-model:showDialog="showCpEditDialog"
              v-model:verbindungsparameter="selectedConnectionParameter"
              v-model:sectionArray="sections"
              v-model:terminalSystemArray="terminalSystems"
              :isNew="isNewConnection"
              @refresh="loadConnectionParameters()"
            ></connection-parameter-dialog>
          </v-stepper-window-item>
        </v-stepper-window>
        <div class="d-flex justify-space-between align-center pa-4">
          <v-btn color="primary" variant="tonal" @click="prev" :disabled="step === 1 || loading"> {{ $vuetify.locale.t('$vuetify.stepper.prev') }} </v-btn>
          <v-btn v-if="step < 2" color="primary" variant="tonal" @click="nextPage()"> {{ $vuetify.locale.t('$vuetify.stepper.next') }} </v-btn>
          <v-btn v-else color="success" variant="flat" prepend-icon="mdi-check" @click="finishOnboarding" :disabled="!isConfigured"> Finish </v-btn>
        </div>
      </template>
    </v-stepper>
  </v-dialog>
</template>
<script setup>
import { ref, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import useNotification from '@/hooks/useNotification.js';
import ConnectionParameterTable from '@/components/ConnectionParameterTable.vue';
import ConnectionParameterDialog from '@/components/ConnectionParameterDialog.vue';
import ConnectionParameterApi from '@/apis/connectionparameter-api.js';
import TerminalSystemTable from '@/components/TerminalSystemTable.vue';
import TerminalSystemDialog from '@/components/dialog/TerminalSystemDialog.vue';
import TerminalApi from '@/apis/terminal-api';
import SectionApi from '@/apis/section-api';
import OnboardingApi from '@/apis/onboarding-api.js';

const { t } = useI18n();
const { setNotification } = useNotification();
const showOnboardingDialog = defineModel('showDialog', { type: Boolean, default: false });
const step = ref(1);
const selection = ref([]);
const loading = ref(true);
const terminalSystems = ref([]);
const connectionParameters = ref([]);
const sections = ref([]);
const system = ref({});
const selectedConnectionParameter = ref({});
const search = ref('');
const page = ref(1);
const showTsEditDialog = ref(false);
const showCpEditDialog = ref(false);
const isNewConnection = ref(false);

const isConfigured = computed(() => {
  return connectionParameters.value.every((item) => {
    return validateConnectionParameter(item);
  });
});

const loadTerminalSystems = async () => {
  loading.value = true;
  try {
    const response = await TerminalApi.GetAllTerminalSystems();
    if (response.status == 200) {
      terminalSystems.value = response.data;
      for (const ts of terminalSystems.value) {
        ts.sectionName = sections.value.find((x) => x.id == ts.sectionId)?.label ?? '';
      }
    }
  } catch (error) {
    console.log('error while fetching badges', error);
  } finally {
    loading.value = false;
  }
};

const loadSections = async () => {
  loading.value = true;
  try {
    const response = await SectionApi.List();
    if (response.status == 200) {
      sections.value = response.data;
    }
  } catch (error) {
    console.log('error while fetching badges', error);
  } finally {
    loading.value = false;
  }
};

const nextPage = async () => {
  if (step.value < 2) {
    step.value++;
  }

  await loadConnectionParameters();
};

const loadConnectionParameters = async () => {
  loading.value = true;
  try {
    let response = await ConnectionParameterApi.ListConnectionParameter();
    if (response.status == 200) {
      connectionParameters.value = response.data;
      connectionParameters.value.forEach(async (item) => {
        item.isConfigured = validateConnectionParameter(item);
        item.hasNoConnection = !(await checkConnection(item));

        if (item?.sectionId === 6) {
          const splittedDormaString = item.url.split(';');
          for (const param of splittedDormaString) {
            var splittedParam = param.split('=');
            if (splittedParam.length !== 2) continue;
            switch (splittedParam[0]) {
              case 'user id':
                item.user = splittedParam[1];
                break;
              case 'host':
                item.url = splittedParam[1];
                break;
              case 'port':
                item.port = splittedParam[1];
                break;
              case 'virtual host':
                item.virtualhost = splittedParam[1];
                break;
              case 'uuid':
                item.uuid = splittedParam[1];
                break;
            }
          }
        }
      });
    }
  } catch (error) {
    console.log('error while fetching connection parameters', error);
  } finally {
    loading.value = false;
  }
};

const saveTerminalSystem = async () => {
  try {
    loading.value = true;
    if (!system.value) return;

    const response = await TerminalApi.SaveTerminalSystem(system.value);
    if (response.status >= 200 && response.status < 300) {
      setNotification({
        message: t('message.success.saveTerminalSystem'),
        icon: 'mdi-check-circle-outline',
        color: 'success',
      });
    } else
      setNotification({
        message: t('message.error.saveTerminalSystem'),
        icon: 'mdi-check-circle-outline',
        color: 'error',
      });
  } catch (error) {
    setNotification({
      message: t('message.error.saveTerminalSystem'),
      icon: 'mdi-check-circle-outline',
      color: 'error',
    });
  } finally {
    await loadTerminalSystems();
    loading.value = false;
  }
};

const validateConnectionParameter = (connectionParameter) => {
  if (!connectionParameter) return false;
  if (!connectionParameter.user || connectionParameter.name?.trim() === '') return false;
  if (!connectionParameter.url || connectionParameter.url?.trim() === '') return false;

  return true;
};

const checkConnection = async (connectionParameter) => {
  if (!connectionParameter) return false;

  try {
    const response = await ConnectionParameterApi.UseConnectionCheck(connectionParameter);
    if (response.status == 200) {
      return response.data.success;
    } else {
      return false;
    }
  } catch (error) {
    console.log('Error while checking connection', error);
    return false;
  }
};

const finishOnboarding = async () => {
  showOnboardingDialog.value = false;
  try {
    await OnboardingApi.OnboardingComplete();
  } catch (error) {
    console.log('Error while finishing onboarding', error);
    setNotification({
      message: t('message.error.onboardingFinished'),
      icon: 'mdi-alert-circle-outline',
      color: 'error',
    });
  }
};

const showTsDialog = () => {
  if (selection.value && selection.value.length > 0) {
    system.value = { ...selection.value[0] };
    showTsEditDialog.value = true;
  }
};

const showCpDialog = () => {
  if (selection.value && selection.value.length > 0) {
    selectedConnectionParameter.value = { ...selection.value[0] };
    showCpEditDialog.value = true;
  }
};

onMounted(async () => {
  await loadSections();
  await loadTerminalSystems();
});
</script>
